// src/components/watch/WatchCard.js
import React, { useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Star,
  Clock,
  Trophy,
  Info,
  Users,
  Share2,
  BarChart2,
} from "lucide-react";
import CryptoIconWrapper from "../cryptoicons/CryptoIconWrapper";
import "./WatchCard.css";

import firstBadge from "../../assets/first-badge.png";
import secondBadge from "../../assets/second-badge.png";
import thirdBadge from "../../assets/third-badge.png";

const BADGES = [firstBadge, secondBadge, thirdBadge];

const WatchCard = ({ race, onWatch }) => {
  const [isFavorite, setIsFavorite] = useState(false);
  const [showTooltip, setShowTooltip] = useState("");
  const [showShareTooltip, setShowShareTooltip] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [error, setError] = useState(null);
  const [elapsedTime, setElapsedTime] = useState({
    hours: 2,
    minutes: 0,
    seconds: 0,
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setElapsedTime((prev) => {
        const totalSeconds = prev.hours * 3600 + prev.minutes * 60 + prev.seconds + 1;
        return {
          hours: Math.floor(totalSeconds / 3600),
          minutes: Math.floor((totalSeconds % 3600) / 60),
          seconds: totalSeconds % 60,
        };
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleFavoriteClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsFavorite((prev) => !prev);
  }, []);

  const handleStats = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowStats((prev) => !prev);
  }, []);

  const handleShare = useCallback(
    async (e) => {
      e.preventDefault();
      e.stopPropagation();

      try {
        if (!navigator.share) {
          throw new Error("Share functionality not available");
        }

        const leadingToken = race.tokens[0];
        await navigator.share({
          title: `${race.title} - Live Crypto Race`,
          text: `Check out this live race: ${race.title}\nCurrent Leader: ${leadingToken?.symbol
            } (${leadingToken?.percentChange > 0 ? "+" : ""}${leadingToken?.percentChange
            }%)`,
          url: window.location.href,
        });
      } catch (err) {
        setError(err.message);
        setShowShareTooltip(true);
        setTimeout(() => {
          setShowShareTooltip(false);
          setError(null);
        }, 2000);
      }
    },
    [race]
  );


  const formatNumber = (number) => String(number).padStart(2, '0');

  return (
    <div className={`race-card watch-card`}>
      <div className="card-header">
        <div className="title-section">
          <div className="title-status">
            <div className="live-indicator">
              <span className="live-dot"></span>
              Live
            </div>
            <h2>{race.title}</h2>
          </div>
          <div className="flex gap-3">
            <div
              className={`stats-buttonD action-buttonD  ${showStats ? "active" : ""}`}
              onClick={handleStats}
              aria-label="Toggle statistics"
            >
              <BarChart2 size={20} />
            </div>
            <div
              className="stats-buttonD action-buttonD "
              onClick={handleShare}
              aria-label="Share race"
            >
              <Share2 size={20} />
              {showShareTooltip && (
                <div className="tooltip">{error || "Share this race"}</div>
              )}
            </div>
            <div
              className={`stats-buttonD action-buttonD  ${isFavorite ? "active" : ""}`}
              onClick={handleFavoriteClick}
              aria-label={
                isFavorite ? "Remove from favorites" : "Add to favorites"
              }
            >
              <Star className={isFavorite ? "filled" : ""} size={20} />
            </div>
          </div>
        </div>
      </div>

      <div className="info-section">
        <div className="flex">
          <div className="prize-pool flex-grow">
            <span className="prize-pool-label">Total Prize Pool</span>
            <span className="amount">
              ${race.totalPrizePool?.toLocaleString() || "0"}
            </span>
          </div>
          <div className="volume-info flex-grow">
            <span className="volume-label">Total Volume</span>
            <span className="volume-amount">
              ${race.totalVolume?.toLocaleString() || "0"}
            </span>
          </div>
          <div className="timer-section">
            <div className="timer-label">Time Elapsed</div>
            <div className="horizontal-timer">
              <div className="time-unit">
                <span className="time-value">
                  {formatNumber(elapsedTime.hours)}
                </span>
                <span className="time-label">Hrs</span>
              </div>
              <div className="time-unit">
                <span className="time-value">
                  {formatNumber(elapsedTime.minutes)}
                </span>
                <span className="time-label">Mins</span>
              </div>
              <div className="time-unit">
                <span className="time-value">
                  {formatNumber(elapsedTime.seconds)}
                </span>
                <span className="time-label">Secs</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="divider mt-8"></div>
      <div className="pb-8 pt-4 relative overflow-hidden">
        <div className="watch-card-purple-circle"></div>
        <div className="flex p-4 flex-col gap-6 z-50 relative">
          {race.tokens.map((token, index) => {
            const momentum =
              Number(token.momentum) || Number(token.percentChange) || 0;
            return (
              <div
                key={token.symbol}
                className={`flex gap-2 items-center`}
              >
                {index < 3 && <img src={BADGES[index]} alt="Badge" />}
                <div className="ml-4">
                  <CryptoIconWrapper name={token.symbol.toLowerCase()} size={32} />
                </div>
                <div className="">{token.symbol || "N/A"}</div>
                <div className="flex-grow text-gray-300 text-sm">{token.name || "Unknown"}</div>
                <>
                  <div className="font-bold">${token.formattedPrice}</div>
                  <div
                    className={`token-change ${momentum >= 0 ? "positive" : "negative"
                      }`}
                  >
                    {momentum > 0 ? "+" : ""}
                    {momentum.toFixed(2)}%
                  </div></>
              </div>
            )
          })}
        </div>
      </div>
      <div className="divider"></div>



      <div className="flex mt-6">
        <div className="flex flex-col flex-grow gap-2">
          <div className="race-duration">
            <Clock size={16} />
            <span>{race.remainingTime || "N/A"} remaining</span>
          </div>
          <div className="race-type">
            <Trophy size={16} />
            <span>{race.handicap || "N/A"}</span>
            <div className="relative">
              <Info
                size={16}
                className="info-icon"
                onMouseEnter={() => setShowTooltip("handicap")}
                onMouseLeave={() => setShowTooltip("")}
              />
              {showTooltip === 'handicap' && (
                <div className="tooltip">
                  Race handicap affects token performance calculation
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="participants-preview">
          <div className="">
            <div className="mt-2">
              <div className="flex items-center gap-1">
                <Users size={16} />
                <span>{race.participants?.length || 0} Active Participants</span>
              </div>
            </div>
            <div className="participants-avatars">
              {race.participants?.slice(0, 3).map((participant, index) => (
                <div
                  key={participant.address}
                  className="avatar"
                  style={{ zIndex: 3 - index }}
                >
                  <img
                    src={participant.avatar}
                    alt={`Participant ${index + 1}`}
                    loading="lazy"
                  />
                  <div className="token-badge">
                    <CryptoIconWrapper
                      name={participant.token.toLowerCase()}
                      size={16}
                    />
                  </div>
                </div>
              ))}
              {race.participants?.length > 3 && (
                <div className="avatar more">+{race.participants.length - 3}</div>
              )}
            </div>
          </div>
        </div>
      </div>

      <button
        className="watch-button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onWatch(); // Call the new watch handler
        }}
        aria-label="Watch live race"
      >
        Watch Live Race
      </button>

    </div>
  );
};

WatchCard.propTypes = {
  race: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    totalPrizePool: PropTypes.number.isRequired,
    totalVolume: PropTypes.number.isRequired,
    elapsedTime: PropTypes.string.isRequired,
    remainingTime: PropTypes.string.isRequired,
    tokens: PropTypes.arrayOf(
      PropTypes.shape({
        symbol: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        percentChange: PropTypes.number.isRequired,
        position: PropTypes.number.isRequired,
        currentPrice: PropTypes.number.isRequired,
        momentum: PropTypes.number,
      })
    ).isRequired,
    participants: PropTypes.arrayOf(
      PropTypes.shape({
        address: PropTypes.string.isRequired,
        avatar: PropTypes.string.isRequired,
        bet: PropTypes.number.isRequired,
        token: PropTypes.string.isRequired,
        joinedAgo: PropTypes.string.isRequired,
      })
    ).isRequired,
    handicap: PropTypes.string.isRequired,
  }).isRequired,

  onWatch: PropTypes.func.isRequired,
};

export default WatchCard;
